import { useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'

/** Subtle tiled coffee-bean pattern used as a page-background decoration. */
function CoffeeBeanPattern() {
  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full text-cream-100/10"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <pattern
          id="coffee-beans"
          width="90"
          height="90"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(12)"
        >
          <g fill="currentColor" stroke="currentColor">
            <ellipse cx="22" cy="22" rx="16" ry="10" transform="rotate(45 22 22)" />
            <path d="M10 22c6-8 18-8 24 0" strokeWidth="2" fill="none" />
          </g>
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#coffee-beans)" />
    </svg>
  )
}

export default function LoginPage() {
  const { user, loading } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (user) {
      if (user.role === 'admin') {
        navigate('/admin')
      } else {
        navigate('/dashboard')
      }
    }
  }, [user, navigate])

  if (loading) {
    return (
      <div className="theme-fixed-light min-h-[100dvh] flex items-center justify-center bg-espresso-900">
        <div className="text-xl text-cream-100">Cargando...</div>
      </div>
    )
  }

  return (
    // theme-fixed-light: this whole screen is a fixed dark-hero brand splash
    // (gradient + floating light card), not a normal theme-reactive surface
    // -- see the class definition in index.css for why.
    <div className="theme-fixed-light relative min-h-[100dvh] overflow-hidden bg-gradient-to-br from-espresso-900 via-espresso-800 to-terracotta-800 text-cream-50">
      <CoffeeBeanPattern />
      <div
        className="pointer-events-none absolute -top-24 -left-24 h-96 w-96 rounded-full bg-terracotta-500/30 blur-3xl"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -bottom-32 -right-16 h-[28rem] w-[28rem] rounded-full bg-cream-300/10 blur-3xl"
        aria-hidden="true"
      />

      <div className="relative z-10 flex min-h-[100dvh] flex-col">
        <header className="flex items-center justify-between px-6 py-5 sm:px-10">
          <div className="flex items-center gap-2 text-xl font-semibold tracking-tight">
            <span aria-hidden="true">☕</span>
            <span>Cafe Distribution</span>
          </div>
          <nav className="flex items-center gap-3 text-sm font-medium">
            <a
              href="#acceso"
              className="rounded-md px-3 py-1.5 text-cream-100 transition hover:text-white focus:outline-none focus:ring-2 focus:ring-cream-200"
            >
              Iniciar sesión
            </a>
            <Link
              to="/register/seller"
              className="rounded-md bg-cream-50 px-3 py-1.5 text-espresso-800 transition hover:bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-espresso-900 focus:ring-cream-200"
            >
              Registrarse
            </Link>
          </nav>
        </header>

        <main className="flex flex-1 items-center px-6 pb-16 sm:px-10">
          <div className="mx-auto grid w-full max-w-5xl gap-10 md:grid-cols-2 md:items-center">
            <section className="space-y-6">
              <p className="inline-block rounded-full bg-cream-50/10 px-3 py-1 text-xs font-medium uppercase tracking-wide text-cream-200">
                Distribución B2B de café · Lima, Perú
              </p>
              <h1 className="text-4xl font-bold leading-tight sm:text-5xl">
                Gestión integral de distribución de café
              </h1>
              <p className="max-w-md text-lg text-cream-100/90">
                Conecta vendedores, rutas y clientes en un solo lugar. Controla
                pedidos, cobranzas y zonas de venta con visibilidad en tiempo
                real.
              </p>
              <ul className="space-y-2 text-sm text-cream-100/80">
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-terracotta-300" aria-hidden="true" />
                  Pedidos y cobranzas centralizados
                </li>
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-terracotta-300" aria-hidden="true" />
                  Zonas de venta y rutas en el mapa
                </li>
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-terracotta-300" aria-hidden="true" />
                  Reportes para vendedores y administradores
                </li>
              </ul>
            </section>

            <section
              id="acceso"
              className="rounded-2xl bg-cream-50 p-8 text-espresso-900 shadow-2xl"
            >
              <div className="text-center">
                <h2 className="text-2xl font-bold text-espresso-800">
                  Accede a tu cuenta
                </h2>
                <p className="mt-1 text-sm text-espresso-600">
                  Elige tu tipo de inicio de sesión
                </p>
              </div>
              <div className="mt-6 space-y-3">
                <Link
                  to="/login/seller"
                  className="block w-full rounded-md bg-terracotta-600 px-4 py-3 text-center text-sm font-medium text-white transition hover:bg-terracotta-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-terracotta-500"
                >
                  Iniciar como Colaborador
                </Link>
                <Link
                  to="/login/admin"
                  className="block w-full rounded-md border border-espresso-200 bg-white px-4 py-3 text-center text-sm font-medium text-espresso-700 transition hover:bg-espresso-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-terracotta-500"
                >
                  Iniciar como Administrador
                </Link>
              </div>
              <p className="mt-6 text-center text-xs text-espresso-500">
                ¿Eres nuevo?{' '}
                <Link
                  to="/register/seller"
                  className="font-medium text-terracotta-700 hover:text-terracotta-800"
                >
                  Registrarse como Colaborador
                </Link>
              </p>
            </section>
          </div>
        </main>
      </div>
    </div>
  )
}
